import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Globe, User, Terminal, Image as ImageIcon, Video } from 'lucide-react';
import Modal_Component from '../../Components/ExtraComponents/Modal';

const GlobalBugReportModal = ({ isOpen, onClose, capturedImage }) => {
    const user_details = JSON.parse(localStorage.getItem("user_details")) || {};
    const user_role = JSON.parse(localStorage.getItem("user_role")) || 'USER';
    
    const [sidebarNames, setSidebarNames] = useState([]);

    useEffect(() => {
       
        import('../../Components/Dashboard/Sidebar/Nav_Config').then(mod => {
             let names = [];
             if (user_role === 'ADMIN') names = mod.admin_sidebar.map(s => s.name);
             else if (user_role === 'SUPERADMIN') names = mod.supper_admin_sidebar.map(s => s.name);
             else if (user_role === 'SUBADMIN') names = mod.sub_admin_sidebar.map(s => s.name);
             else names = mod.Client.map(s => s.name);
             setSidebarNames(names);
        });
    }, [user_role]);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        domain: window.location.hostname,
        pageUrl: window.location.href,
        projectName: 'Crypto',
        role: user_role || 'User',
        userName: user_details?.UserName || 'Guest',
        userId: user_details?.user_id || user_details?._id || '',
        bugType: '', 
        ticketType: 'Bug',
        errorStack: '',
        priority: 'Low',
        browserInfo: navigator.userAgent,
        deviceInfo: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        moduleName: window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname
    });

    const [loading, setLoading] = useState(false);
    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);

    useEffect(() => {
        if (isOpen) {
            const currentUrl = window.location.hash ? window.location.hash.replace('#', '') : window.location.pathname;
            setFormData(prev => ({
                ...prev,
                pageUrl: window.location.href,
                moduleName: currentUrl,
                bugType: sidebarNames[0] || ''
            }));
            if (capturedImage) {
                setImages([capturedImage]);
            } else {
                setImages([]);
            }
        } else {
            setImages([]);
            setVideo(null);
            setFormData(prev => ({ ...prev, title: '', description: '', bugType: sidebarNames[0] || '', ticketType: 'Bug' }));
        }
    }, [isOpen, capturedImage, sidebarNames]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (name === 'images') {
            const selectedFiles = Array.from(files);
            if (capturedImage) {
                setImages([capturedImage, ...selectedFiles]);
            } else {
                setImages(selectedFiles);
            }
        }
        if (name === 'video') setVideo(files[0]);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!formData.title || !formData.description) {
            toast.error('Title and Description are required');
            return;
        }

        setLoading(true);

        try {
            let response;
            if (images.length > 0 || video) {
                const data = new FormData();
                Object.keys(formData).forEach(key => {
                    data.append(key, formData[key]);
                });
                images.forEach(img => {
                    data.append('attachments', img);
                });
                if (video) data.append('attachments', video);

                const res = await fetch('https://ticket.tradestreet.in/backend/api/common/report-bug', {
                    method: 'POST',
                    body: data
                });
                const result = await res.json();
                response = { data: result };
            } else {
                response = await axios.post('https://ticket.tradestreet.in/backend/api/common/report-bug', formData);
            }

            if (response.data.success || response.data.status) {
                toast.success('Bug reported successfully! Our team will look into it.');
                onClose(true); // true to indicate success
            } else {
                toast.error(response.data.msg || response.data.message || 'Failed to report bug');
            }
        } catch (error) {
            console.error('Error reporting bug:', error);
            toast.error('Error reporting bug. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal_Component
            isOpen={isOpen}
            handleClose={() => onClose(false)}
            title="Report New Bug"
            btn_name="Submit Bug Report"
            Submit_Function={handleSubmit}
            disabled_submit={loading}
            size="lg"
        >
            <form id="bug-report-form" onSubmit={handleSubmit}>
                <div className="row g-3">
                    <div className="col-md-5">
                        <label className="form-label small fw-bold">Bug Title *</label>
                        <input
                            type="text"
                            className="form-control border-light shadow-none bg-light bg-opacity-50 "
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Brief summary of the issue"
                            required
                        />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label small fw-bold">Ticket Type</label>
                        <select
                            className="form-select border-light shadow-none bg-light bg-opacity-50"
                            name="ticketType"
                            value={formData.ticketType}
                            onChange={handleChange}
                        >
                            <option value="Bug">Bug</option>
                            <option value="Update">Updation</option>
                            <option value="Add License">Add License</option>
                            <option value="Add IP">Add IP</option>
                        </select>
                    </div>

                    <div className="col-md-3">
                        <label className="form-label small fw-bold">Priority</label>
                        <select
                            className="form-select border-light shadow-none bg-light bg-opacity-50"
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                        >
                            <option value="Low">Low</option>
                            {/* <option value="Medium">Medium</option> */}
                            <option value="High">High</option>
                            {/* <option value="Critical">Critical</option> */}
                        </select>
                    </div>

                    <div className="col-12">
                        <label className="form-label small fw-bold">Description *</label>
                        <textarea
                            className="form-control border-light shadow-none bg-light bg-opacity-50"
                            rows="3"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Steps to reproduce, expected vs actual behavior..."
                            required
                        ></textarea>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Bug Type</label>
                        <select
                            className="form-select border-light shadow-none bg-light bg-opacity-50"
                            name="bugType"
                            value={formData.bugType}
                            onChange={handleChange}
                        >
                            <option value="">Select Bug Type</option>
                            {sidebarNames.map((name, idx) => (
                                <option key={idx} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Module (Auto-filled URL)</label>
                        <input
                            type="text"
                            className="form-control border-light shadow-none bg-light bg-opacity-50"
                            name="moduleName"
                            value={formData.moduleName}
                            onChange={handleChange}
                            placeholder="e.g. Dashboard"
                        />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label small fw-bold d-flex align-items-center gap-2">
                            <ImageIcon size={16} className="text-primary" />
                            <span>Screenshot / Image (Multiple)</span>
                        </label>
                        <input
                            type="file"
                            className="form-control border-light shadow-none bg-light bg-opacity-50"
                            name="images"
                            accept="image/*"
                            multiple
                            onChange={handleFileChange}
                        />
                        {images && images.length > 0 && (
                            <div className="small text-success mt-1 mb-0">
                                ✓ {images.length} file(s) selected
                                {images[0]?.name?.includes('screenshot') && ' (Auto-captured included)'}
                            </div>
                        )}
                    </div>

                    <div className="col-md-6">
                        <label className="form-label small fw-bold d-flex align-items-center gap-2">
                            <Video size={16} className="text-primary" />
                            <span>Video Recording</span>
                        </label>
                        <input
                            type="file"
                            className="form-control border-light shadow-none bg-light bg-opacity-50"
                            name="video"
                            accept="video/*"
                            onChange={handleFileChange}
                        />
                        {video && <p className="small text-success mt-1 mb-0">✓ {video.name}</p>}
                    </div>

                    
                </div>
            </form>
        </Modal_Component>
    );
};

export default GlobalBugReportModal;
